<template>
    <CodeDiff
        v-if="contentFlavor === ContentFlavor.TEXTUAL"
        :old-string="fromAsText"
        :new-string="toAsText"
        :filename="textByViewType[viewType].from"
        :newFilename="textByViewType[viewType].to"
        diffStyle="char"
        hideStat
        noDiffLineFeed
        :hideHeader="diffFormat === 'line-by-line'"
        :trim="trim"
        :output-format="diffFormat"
        v-bind="$attrs"
    />
    <ProcessDefinitionDiff
        v-else-if="mimetype === 'application/bpmn+xml'"
        :from="fromAsText"
        :to="toAsText"
        :fromName="textByViewType[viewType].from"
        :toName="textByViewType[viewType].to"
        :showAsDiff="Boolean(fromAsText)"
    />
</template>

<script lang="ts" setup>
import { CodeDiff } from "v-code-diff";
import { watch, computed, ref } from "vue";
import { getMimetypeFlavor, ContentFlavor } from "@/lib/contentFlavor";
import ProcessDefinitionDiff from "./ProcessDefinitionDiff.vue";

type ViewType = "history" | "draft";

const props = withDefaults(
    defineProps<{
        from: Blob;
        to: Blob;
        viewType?: ViewType;
        trim?: boolean;
        mimetype?: string;
        diffFormat?: "side-by-side" | "line-by-line";
    }>(),
    {
        viewType: "history",
        name: "",
        newName: "",
        trim: true,
        mimetype: "",
        diffFormat: "side-by-side",
    }
);

const textByViewType: Record<ViewType, { to: string; from: string }> = {
    history: {
        from: "Previous version",
        to: "Selected version",
    },
    draft: {
        from: "Current version",
        to: "Your local draft",
    },
};

const contentFlavor = computed(() => {
    return getMimetypeFlavor(props.mimetype);
});

const fromAsText = ref("");
const toAsText = ref("");

watch(
    () => props.from,
    async () => {
        switch (contentFlavor.value) {
            case ContentFlavor.TEXTUAL:
            case ContentFlavor.DIAGRAM: // BPMN is a diagram
                fromAsText.value = await props.from.text();
        }
    },
    { immediate: true }
);

watch(
    () => props.to,
    async () => {
        switch (contentFlavor.value) {
            case ContentFlavor.TEXTUAL:
            case ContentFlavor.DIAGRAM: // BPMN is a diagram
                toAsText.value = await props.to.text();
        }
    },
    { immediate: true }
);
</script>
